import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";

const statusColors = {
  available: "bg-emerald-50 text-emerald-700",
  reserved: "bg-amber-50 text-amber-700",
  occupied: "bg-blue-50 text-blue-700",
  maintenance: "bg-gray-100 text-gray-500",
};

const defaultForm = { number: "", floor: "1", category: "single", price: "", deposit: "", capacity: "1", description: "", amenities: "" };

export default function AdminRoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchRooms = () => {
    api.get("/rooms?status=").then((r) => setRooms(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchRooms(); }, []);

  const openAdd = () => { setEditing(null); setForm(defaultForm); setDialogOpen(true); };

  const openEdit = (room) => {
    setEditing(room);
    setForm({
      number: room.number, floor: String(room.floor), category: room.category,
      price: String(room.price), deposit: String(room.deposit), capacity: String(room.capacity),
      description: room.description || "", amenities: (room.amenities || []).join(", "),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      number: form.number, floor: parseInt(form.floor), category: form.category,
      price: parseFloat(form.price), deposit: parseFloat(form.deposit), capacity: parseInt(form.capacity),
      description: form.description,
      amenities: form.amenities.split(",").map((a) => a.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/rooms/${editing._id}`, payload);
        toast.success("Room updated");
      } else {
        await api.post("/admin/rooms", payload);
        toast.success("Room created");
      }
      setDialogOpen(false);
      fetchRooms();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await api.delete(`/admin/rooms/${id}`);
      toast.success("Room deleted");
      fetchRooms();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const stats = {
    total: rooms.length,
    available: rooms.filter((r) => r.status === "available").length,
    occupied: rooms.filter((r) => r.status === "occupied").length,
    reserved: rooms.filter((r) => r.status === "reserved").length,
  };

  return (
    <DashboardLayout title="Room Management">
      <div className="space-y-6" data-testid="admin-rooms-page">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total", value: stats.total },
            { label: "Available", value: stats.available },
            { label: "Occupied", value: stats.occupied },
            { label: "Reserved", value: stats.reserved },
          ].map((s) => (
            <Card key={s.label} className="border-border/60">
              <CardContent className="pt-6 text-center">
                <div className="font-heading text-2xl font-bold text-[#0A2540]">{s.value}</div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">{rooms.length} rooms total</p>
          <Button className="bg-[#E25822] hover:bg-[#E25822]/90 text-white" onClick={openAdd} data-testid="add-room-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Room
          </Button>
        </div>

        {/* Table */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Room</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Floor</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rooms.map((room) => (
                    <TableRow key={room._id} data-testid={`admin-room-row-${room.number}`}>
                      <TableCell className="font-medium">{room.number}</TableCell>
                      <TableCell className="capitalize">{room.category}</TableCell>
                      <TableCell>{room.floor}</TableCell>
                      <TableCell>${room.price}/mo</TableCell>
                      <TableCell><Badge className={statusColors[room.status]}>{room.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(room)} data-testid={`edit-room-${room.number}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(room._id)} data-testid={`delete-room-${room.number}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="room-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Edit Room" : "Add Room"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Room Number</Label>
                  <Input value={form.number} onChange={update("number")} className="h-11 mt-1" required data-testid="room-number-input" />
                </div>
                <div>
                  <Label>Floor</Label>
                  <Input type="number" min="1" value={form.floor} onChange={update("floor")} className="h-11 mt-1" required />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="accessible">Accessible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input type="number" min="1" value={form.capacity} onChange={update("capacity")} className="h-11 mt-1" required />
                </div>
                <div>
                  <Label>Price ($/mo)</Label>
                  <Input type="number" step="0.01" min="0" value={form.price} onChange={update("price")} className="h-11 mt-1" required />
                </div>
                <div>
                  <Label>Deposit ($)</Label>
                  <Input type="number" step="0.01" min="0" value={form.deposit} onChange={update("deposit")} className="h-11 mt-1" required />
                </div>
              </div>
              <div>
                <Label>Amenities (comma separated)</Label>
                <Input value={form.amenities} onChange={update("amenities")} className="h-11 mt-1" placeholder="WiFi, Desk, Bathroom" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={update("description")} className="mt-1" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#0A2540] text-white" disabled={submitting} data-testid="save-room-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Create Room"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
